import React, { useContext, useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { FlowContext } from '../../core/flow-engine';
import { CartContextProvider } from '../../context/CartContext/CartContext';
import { RewardsContext } from '../../context/RewardsContext/RewardsContext';
import { ALL_FUNDS_QUERY } from '../../components/FundListFragment/queries';
import FONASUCustomerInformation from '../../components/FONASUCustomerInformation/FONASUCustomerInformation';
import FONASUPortfolioAllocation from '../../components/FONASUPortfolioAllocation/FONASUPortfolioAllocation';
import FONASUCreateCredentials from '../../components/FONASUCreateCredentials/FONASUCreateCredentials';
import { formatFunds } from '../../flows/shared/utils';
import FONASUHelpText from '../../components/FONASUHelpText/FONASUHelpText';
import styles from './FONASUMFTaxableUgmaUtmaScreen.css';

import Heading from '../../components/Typography/Heading/Heading';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import AlertMessage from '../../components/AlertMessage/AlertMessage';
import greenCheck from '../../images/green-checkmark.svg';
import { isPublishNotAllowed } from '../FONASUMFTransferScreen/util';
import FONASUMinorInformation from '../../components/FONASUMinorInformation/FONASUMinorInformation';
import AlertMessageWithButton from '../../components/AlertMessageWithButton/AlertMessageWithButton';

const FONASUMFTaxableUgmaUtmaScreenContents = ({
  children,
  showSuccess,
  editCustomer,
  setEditCustomer,
  editPortfolio,
  setEditPortfolio,
  editMinorInfo,
  setEditMinorInfo,
  showCredentialsSection,
  isValid,
  next,
  alert,
  error
}) => {
  const { data: { allMutualFunds = [] } = {} } = useQuery(ALL_FUNDS_QUERY);

  const pageTitle = `Open an UGMA/UTMA Account`;

  const { rewardsAvailable } = useContext(RewardsContext);

  const { closedFunds: closedFundsReward } = rewardsAvailable;

  const TaxableMutualFunds = closedFundsReward
    ? allMutualFunds.filter(
        ({ openToNewInvestors, openToSubsequentInvestments }) =>
          openToNewInvestors ||
          (!openToNewInvestors && openToSubsequentInvestments)
      ) /* remove suppressed fund(s) regardless of  summit rewards */
    : allMutualFunds.filter(({ openToNewInvestors }) => openToNewInvestors);

  return (
    <>
      {alert && (
        <AlertMessage message="The publish to web function failed and the information was not submitted. Please try again." />
      )}
      <AlertMessageWithButton
        error={error}
        onClick={next('deleteApplication')}
        btnStyle="continue-button"
        btnText="Continue"
      />
      <div className="wrapper">
        {showSuccess && (
          <div className="submit-result-message">
            <img className="green-check" src={greenCheck} alt="Success" />{' '}
            Submitted!
          </div>
        )}
        <Heading text={pageTitle} />
        <FONASUHelpText />

        <div className="review-section">
          <FONASUCustomerInformation
            showSuccess={showSuccess}
            editCustomer={editCustomer}
            setEditCustomer={setEditCustomer}
            showInvestmentAmt
          />
        </div>

        <div className="review-section">
          <FONASUMinorInformation
            showSuccess={showSuccess}
            editMinorInfo={editMinorInfo}
            setEditMinorInfo={setEditMinorInfo}
          />
        </div>

        <div className="review-section">
          <FONASUPortfolioAllocation
            showSuccess={showSuccess}
            editPortfolio={editPortfolio}
            setEditPortfolio={setEditPortfolio}
            allMutualFunds={TaxableMutualFunds}
            rewardsAvailable={rewardsAvailable}
            next={next}
            error={error}
          />
        </div>

        {showCredentialsSection && (
          <div className="review-section">
            <FONASUCreateCredentials
              showSuccess={showSuccess}
              isValid={isValid}
            />
          </div>
        )}

        <div className="btn-container">{children}</div>

        <style jsx>{styles}</style>
      </div>
    </>
  );
};

const checkValid = (infoValid, minorInfoValid, portfolioValid) =>
  infoValid && minorInfoValid && portfolioValid;

const doneMode = (editCustomer, editMinorInfo, editPortfolio) =>
  !(editCustomer || editMinorInfo || editPortfolio);

const shouldShowCredentialsSection = usernameExists => !usernameExists;

const FONASUMFTaxableUgmaUtmaScreen = ({ appState }) => {
  const { next, alert } = useContext(FlowContext);
  const {
    store: {
      app: { newInvestments: selectedFunds, profileApplicationID },
      amount,
      accountType,
      loadedData: {
        infoValid,
        minorInfoValid,
        portfolioValid,
        usernameExists,
        error,
        customerInfoEditMode,
        minorEditMode,
        portfolioEditMode,
        loginID
      }
    }
  } = appState;

  const [showSuccess, setShowSuccess] = useState(false);
  const [editCustomer, setEditCustomer] = useState(customerInfoEditMode);
  const [editPortfolio, setEditPortfolio] = useState(portfolioEditMode);
  const [editMinorInfo, setEditMinorInfo] = useState(minorEditMode);

  useEffect(() => {
    if (profileApplicationID) setShowSuccess(true);
  }, [profileApplicationID]);

  const isValid =
    checkValid(infoValid, minorInfoValid, portfolioValid) &&
    doneMode(editCustomer, editMinorInfo, editPortfolio);

  const disablePublish = isPublishNotAllowed(
    showSuccess,
    isValid,
    loginID,
    error
  );

  const showCredentialsSection = shouldShowCredentialsSection(usernameExists);

  const formattedSelectedFunds = formatFunds(selectedFunds);

  const handleClick = async () => {
    if (isValid && !showSuccess) {
      next({
        action: 'publish'
      })();
    }
  };

  return (
    <CartContextProvider
      initialTotalAmount={amount}
      initialPercentMode
      initialFunds={formattedSelectedFunds}>
      <FONASUMFTaxableUgmaUtmaScreenContents
        editCustomer={editCustomer}
        setEditCustomer={setEditCustomer}
        editPortfolio={editPortfolio}
        setEditPortfolio={setEditPortfolio}
        showSuccess={showSuccess}
        setShowSuccess={setShowSuccess}
        editMinorInfo={editMinorInfo}
        setEditMinorInfo={setEditMinorInfo}
        showCredentialsSection={showCredentialsSection}
        isValid={isValid}
        next={next}
        alert={alert}
        error={error}
        accountType={accountType}>
        <MFBeaconButton
          data-name="taxableUgmaPublishToWebBtn"
          disabled={disablePublish}
          onClick={handleClick}
          className="beacon-button-margin-override"
          variant="primary"
          label="Publish to Web"
        />
      </FONASUMFTaxableUgmaUtmaScreenContents>
    </CartContextProvider>
  );
};

export default FONASUMFTaxableUgmaUtmaScreen;
